/**
 * Trig Graph Utilities
 *
 * Core functions for parsing, evaluating, and generating trig graph variations
 * for the hw_questions trig graph selection feature.
 *
 * Trig functions have the form: y = A * func(Bx + C) + D
 * e.g., y = 2tan(3x), f(x) = -3cos(πx/2 - π/4) + 1
 */

// ============================================
// CONSTANTS & DEFAULTS
// ============================================

export const TRIG_FUNC_TYPES = ['sin', 'cos', 'tan', 'cot', 'csc', 'sec'];

export const defaultTrigGraphConfig = {
  type: 'trigGraphSelection',
  equation: '',
  parsedTrig: null,
  step1Types: ['period'],
  grid: {
    xMin: -7,
    xMax: 7,
    yMin: -5,
    yMax: 5,
    xStep: 1,
    yStep: 1,
    showGridLines: true,
    showAxisLabels: true,
  },
  interceptTolerance: 0.05,
  variationSeed: null,
  correctGraphIndex: 0,
};

// Step1 types available per function category
export const STEP1_TYPES_BY_FUNC = {
  sin: ['amplitude', 'period', 'phaseShift', 'verticalShift', 'yIntercept'],
  cos: ['amplitude', 'period', 'phaseShift', 'verticalShift', 'yIntercept'],
  tan: ['period', 'phaseShift', 'verticalShift', 'verticalAsymptotes'],
  cot: ['period', 'phaseShift', 'verticalShift', 'verticalAsymptotes'],
  csc: ['period', 'phaseShift', 'verticalShift', 'verticalAsymptotes'],
  sec: ['period', 'phaseShift', 'verticalShift', 'verticalAsymptotes'],
};

export const STEP1_TYPE_LABELS = {
  amplitude: 'Amplitude',
  period: 'Period',
  phaseShift: 'Phase Shift',
  verticalShift: 'Vertical Shift',
  yIntercept: 'Y-Intercept',
  verticalAsymptotes: 'Vertical Asymptotes',
};

// ============================================
// PARSING UTILITIES
// ============================================

/**
 * Parse a fraction string like "1/2", "-3/4", or regular numbers
 */
export function parseFraction(str) {
  if (typeof str === 'number') return str;
  if (!str || typeof str !== 'string') return NaN;

  const trimmed = str.trim();
  if (trimmed.includes('/')) {
    const parts = trimmed.split('/');
    if (parts.length !== 2) return NaN;
    const num = parseFloat(parts[0]);
    const den = parseFloat(parts[1]);
    if (den === 0) return NaN;
    return num / den;
  }
  return parseFloat(trimmed);
}

/**
 * Parse a pi-aware fraction string.
 * Handles: "pi", "π", "2pi", "pi/2", "2pi/3", "-3pi/4", "-pi", plain numbers
 */
export function parsePiFraction(str) {
  if (typeof str === 'number') return str;
  if (!str || typeof str !== 'string') return NaN;

  let trimmed = str.trim();

  // Normalize π to pi
  trimmed = trimmed.replace(/π/g, 'pi');

  // If no pi, fall back to regular fraction parsing
  if (!trimmed.includes('pi')) {
    return parseFraction(trimmed);
  }

  // Handle forms like: "2pi/3", "-pi/4", "pi", "3pi", "-2pi", "pi/6"
  // General pattern: [coefficient]pi[/denominator]

  // Split on "pi"
  const parts = trimmed.split('pi');
  if (parts.length !== 2) return NaN;

  let coeffStr = parts[0].trim();
  let denomStr = parts[1].trim();

  // Parse coefficient before pi
  let coeff = 1;
  if (coeffStr === '' || coeffStr === '+') {
    coeff = 1;
  } else if (coeffStr === '-') {
    coeff = -1;
  } else {
    // Handle "2/3pi" format (fraction coefficient before pi)
    coeff = parseFraction(coeffStr);
    if (isNaN(coeff)) return NaN;
  }

  // Parse denominator after pi (e.g., "/3", "/4")
  let denom = 1;
  if (denomStr === '') {
    denom = 1;
  } else if (denomStr.startsWith('/')) {
    denom = parseFraction(denomStr.substring(1));
    if (isNaN(denom) || denom === 0) return NaN;
  } else {
    return NaN;
  }

  return (coeff * Math.PI) / denom;
}

/**
 * Normalize LaTeX input to plain form the parser understands.
 * Handles \frac, \tan/\sin/..., \pi, \left/\right, \cdot, etc.
 */
export function normalizeLatexInput(input) {
  if (!input || typeof input !== 'string') return input;
  let s = input;
  // Strip \left and \right (parenthesis sizing)
  s = s.replace(/\\left\s*/g, '').replace(/\\right\s*/g, '');
  // \frac{a}{b} → a/b (run twice to handle simple nesting)
  const fracRe = /\\frac\s*\{([^{}]*)\}\s*\{([^{}]*)\}/g;
  s = s.replace(fracRe, '$1/$2');
  s = s.replace(fracRe, '$1/$2');
  // \pi → pi
  s = s.replace(/\\pi\b/g, 'pi');
  // \sin, \cos, \tan, \cot, \csc, \sec → bare names
  s = s.replace(/\\(sin|cos|tan|cot|csc|sec)\b/g, '$1');
  // \cdot, \times → empty (implicit multiplication)
  s = s.replace(/\\cdot\s*/g, '').replace(/\\times\s*/g, '');
  // Drop stray latex space commands
  s = s.replace(/\\[,;:!> ]/g, '');
  return s;
}

/**
 * Strip balanced outer parentheses, e.g. "(1/2)" -> "1/2", "((pi/2))" -> "pi/2".
 * Leaves non-wrapping parens intact, e.g. "(1+2)(3)" is returned unchanged.
 */
function stripOuterParens(str) {
  let s = str.trim();
  while (s.startsWith('(') && s.endsWith(')')) {
    let depth = 0;
    let wraps = true;
    for (let i = 0; i < s.length; i++) {
      if (s[i] === '(') depth++;
      else if (s[i] === ')') {
        depth--;
        if (depth === 0 && i < s.length - 1) { wraps = false; break; }
      }
    }
    if (!wraps) break;
    s = s.slice(1, -1).trim();
  }
  return s;
}

/**
 * Parse a trig equation string into its components.
 *
 * Supported formats:
 *   y = 2sin(3x)
 *   f(x) = -3cos(πx/2 - π/4) + 1
 *   y = tan(2x - pi/3)
 *   f(x) = 2csc(x) - 1
 *   y = -1/2 sec(2x)
 *
 * @param {string} equation
 * @returns {Object} parsedTrig
 */
export function parseTrigEquation(equation) {
  if (!equation || typeof equation !== 'string') {
    throw new Error('Equation must be a non-empty string');
  }

  // Normalize LaTeX syntax (\frac, \tan, \pi, \left/\right, ...) to plain form
  let eq = normalizeLatexInput(equation).trim();
  // Replace π with pi for parsing
  eq = eq.replace(/π/g, 'pi');

  // Extract RHS after = sign
  const eqIdx = eq.indexOf('=');
  if (eqIdx === -1) throw new Error('Equation must contain "="');
  let rhs = eq.substring(eqIdx + 1).trim();

  // Extract vertical shift D: look for +/- number AFTER the closing paren of the trig function
  let D = 0;
  // Find the trig function and its closing paren
  const trigFuncMatch = rhs.match(/(sin|cos|tan|cot|csc|sec)\s*\(/i);
  if (!trigFuncMatch) throw new Error('No trig function found (sin, cos, tan, cot, csc, sec)');

  const funcType = trigFuncMatch[1].toLowerCase();
  const funcStart = trigFuncMatch.index;

  // Find the matching closing paren
  const openParenIdx = rhs.indexOf('(', funcStart);
  let depth = 0;
  let closeParenIdx = -1;
  for (let i = openParenIdx; i < rhs.length; i++) {
    if (rhs[i] === '(') depth++;
    if (rhs[i] === ')') {
      depth--;
      if (depth === 0) { closeParenIdx = i; break; }
    }
  }
  if (closeParenIdx === -1) throw new Error('Unmatched parenthesis');

  // Extract vertical shift after close paren
  const afterParen = rhs.substring(closeParenIdx + 1).trim();
  if (afterParen) {
    D = parsePiFraction(afterParen);
    if (isNaN(D)) {
      // Try parsing just the numeric part
      const shiftMatch = afterParen.match(/^([+-]?\s*[\d./piπ]+)/);
      if (shiftMatch) {
        D = parsePiFraction(shiftMatch[1].replace(/\s+/g, ''));
        if (isNaN(D)) D = 0;
      } else {
        D = 0;
      }
    }
  }

  // Extract leading coefficient A: everything before the trig function name
  let beforeFunc = rhs.substring(0, funcStart).trim();
  // Handle leading sign before a parenthesized coefficient, e.g. "-(1/2)"
  let signPrefix = '';
  if (beforeFunc.startsWith('+') || beforeFunc.startsWith('-')) {
    signPrefix = beforeFunc[0];
    const rest = beforeFunc.slice(1).trim();
    if (rest.startsWith('(')) beforeFunc = signPrefix + stripOuterParens(rest);
  } else {
    beforeFunc = stripOuterParens(beforeFunc);
  }
  let A = 1;
  if (beforeFunc === '' || beforeFunc === '+') {
    A = 1;
  } else if (beforeFunc === '-') {
    A = -1;
  } else {
    A = parsePiFraction(beforeFunc);
    if (isNaN(A)) throw new Error(`Cannot parse coefficient: "${beforeFunc}"`);
  }

  // Extract argument inside parentheses: Bx + C
  const argStr = rhs.substring(openParenIdx + 1, closeParenIdx).trim();
  const { B, C } = parseTrigArgument(argStr);

  // Calculate derived properties
  const amplitude = Math.abs(A);
  const isTanCot = funcType === 'tan' || funcType === 'cot';
  const period = isTanCot ? Math.PI / Math.abs(B) : (2 * Math.PI) / Math.abs(B);
  const phaseShift = -C / B;
  const verticalShift = D;

  // Calculate y-intercept: f(0) = A * func(C) + D
  const funcMap = {
    sin: Math.sin, cos: Math.cos, tan: Math.tan,
    cot: (a) => 1 / Math.tan(a),
    csc: (a) => 1 / Math.sin(a),
    sec: (a) => 1 / Math.cos(a),
  };
  const yIntercept = A * funcMap[funcType](C) + D;

  return {
    funcType,
    A,
    B,
    C,
    D,
    amplitude,
    period,
    phaseShift,
    verticalShift,
    yIntercept: isFinite(yIntercept) ? yIntercept : undefined,
    originalEquation: equation,
  };
}

/**
 * Parse the argument of a trig function: "Bx + C"
 *
 * Handles forms like:
 *   "3x"          -> B=3, C=0
 *   "x"           -> B=1, C=0
 *   "-x"          -> B=-1, C=0
 *   "2x - pi/3"   -> B=2, C=-pi/3
 *   "pix/2"       -> B=pi/2, C=0
 *   "pix/2 - pi/4"-> B=pi/2, C=-pi/4
 *   "x + 1"       -> B=1, C=1
 */
function parseTrigArgument(argStr) {
  if (!argStr || !argStr.trim()) throw new Error('Empty trig function argument');

  let s = argStr.trim();

  // Find x position
  const xIdx = s.indexOf('x');
  if (xIdx === -1) throw new Error('Trig argument must contain x');

  // Extract B: everything before x (including possible "pi" and fractions)
  let bStr = s.substring(0, xIdx).trim();

  // Extract C: everything after x
  let cStr = s.substring(xIdx + 1).trim();

  // Parse B
  let B;
  // Strip wrapping parens around the coefficient, handling an optional leading sign
  // e.g. "(pi/2)" → "pi/2", "-(pi/2)" → "-pi/2"
  if (bStr.startsWith('+') || bStr.startsWith('-')) {
    const sign = bStr[0];
    const rest = bStr.slice(1).trim();
    if (rest.startsWith('(')) bStr = sign + stripOuterParens(rest);
  } else if (bStr.startsWith('(')) {
    bStr = stripOuterParens(bStr);
  }
  if (bStr === '' || bStr === '+') {
    B = 1;
  } else if (bStr === '-') {
    B = -1;
  } else {
    // Handle "pi/" before x, like "pix/2" → bStr="pi", cStr="/2"
    // But we need to check if cStr starts with "/" which means it's part of B
    if (bStr.endsWith('pi') && cStr.startsWith('/')) {
      // e.g., argStr = "pix/2 - pi/4" → bStr="pi", cStr="/2 - pi/4"
      // Extract the denominator from cStr
      const denomMatch = cStr.match(/^\/(\d+)/);
      if (denomMatch) {
        bStr = bStr + '/' + denomMatch[1];
        cStr = cStr.substring(denomMatch[0].length).trim();
      }
    }
    B = parsePiFraction(bStr);
    if (isNaN(B)) throw new Error(`Cannot parse B coefficient: "${bStr}"`);
  }

  // Parse C
  let C = 0;
  if (cStr === '') {
    C = 0;
  } else {
    // cStr might be like "+ pi/3", "- pi/4", "+ 2", "- 1"
    // Normalize: ensure sign is attached
    let cNormalized = cStr.replace(/\s+/g, '');
    // If it starts with + or -, that's the sign of C
    if (!cNormalized.startsWith('+') && !cNormalized.startsWith('-')) {
      // Check if it's a division that was part of B (already handled above)
      // Otherwise it's an error or implicit +
      if (cNormalized.startsWith('/')) {
        // This might be division for B, but we handled that above
        throw new Error(`Unexpected format in trig argument: "${argStr}"`);
      }
      cNormalized = '+' + cNormalized;
    }
    C = parsePiFraction(cNormalized);
    if (isNaN(C)) throw new Error(`Cannot parse C constant: "${cStr}"`);
  }

  return { B, C };
}

// ============================================
// EVALUATION
// ============================================

const TRIG_FUNC_MAP = {
  sin: Math.sin,
  cos: Math.cos,
  tan: Math.tan,
  cot: (a) => 1 / Math.tan(a),
  csc: (a) => 1 / Math.sin(a),
  sec: (a) => 1 / Math.cos(a),
};

/**
 * Evaluate the trig function at a given x value.
 */
export function evaluateTrigAt(parsedTrig, x) {
  if (!parsedTrig) return NaN;
  const { funcType, A, B, C, D } = parsedTrig;
  const arg = B * x + C;
  return A * TRIG_FUNC_MAP[funcType](arg) + D;
}

// ============================================
// ASYMPTOTE COMPUTATION
// ============================================

/**
 * Compute vertical asymptote x-values within [xMin, xMax].
 *
 * tan(Bx+C): where Bx+C = π/2 + nπ  →  x = (π/2 + nπ - C) / B
 * cot(Bx+C): where Bx+C = nπ         →  x = (nπ - C) / B
 * csc(Bx+C): where sin(Bx+C) = 0     →  x = (nπ - C) / B
 * sec(Bx+C): where cos(Bx+C) = 0     →  x = (π/2 + nπ - C) / B
 *
 * sin/cos have no vertical asymptotes.
 */
export function computeAsymptotes(parsedTrig, xMin, xMax) {
  if (!parsedTrig || !parsedTrig.B) return [];
  const { funcType, B, C } = parsedTrig;

  if (funcType === 'sin' || funcType === 'cos') return [];

  let offset; // base value where the denominator is zero
  if (funcType === 'tan' || funcType === 'sec') {
    offset = Math.PI / 2; // Bx+C = π/2 + nπ
  } else {
    // cot, csc
    offset = 0; // Bx+C = nπ
  }

  const asymptotes = [];
  // Find range of n values
  const nMin = Math.floor(((B * xMin + C) - offset) / Math.PI) - 1;
  const nMax = Math.ceil(((B * xMax + C) - offset) / Math.PI) + 1;

  for (let n = nMin; n <= nMax; n++) {
    const x = (offset + n * Math.PI - C) / B;
    if (x >= xMin - 0.001 && x <= xMax + 0.001) {
      asymptotes.push(x);
    }
  }

  return asymptotes.sort((a, b) => a - b);
}

/**
 * Compute vertical asymptote x-values within one period starting from the phase shift.
 * Used for step1 validation where students identify asymptotes within one period.
 */
export function computeAsymptotesInOnePeriod(parsedTrig) {
  if (!parsedTrig) return [];
  const { period, phaseShift } = parsedTrig;
  const start = phaseShift;
  const end = phaseShift + period;
  // Use slightly expanded range to catch asymptotes at boundaries
  return computeAsymptotes(parsedTrig, start - 0.001, end + 0.001);
}

// ============================================
// CURVE ELEMENT GENERATION
// ============================================

/**
 * Generate a curve element with pre-computed points for rendering on CoordinateGrid.
 * Follows the same pattern as rationalToCurveElement.
 */
export function trigToCurveElement(parsedTrig, gridBounds, numSamples = 500) {
  if (!parsedTrig) {
    return { type: 'curve', curveType: 'trig', points: [], id: `trig_empty_${Date.now()}` };
  }
  const { xMin = -7, xMax = 7 } = gridBounds || {};
  const asymptoteXValues = computeAsymptotes(parsedTrig, xMin, xMax);

  const xValues = new Set();
  const step = (xMax - xMin) / numSamples;

  if (!step || step <= 0) {
    return {
      type: 'curve', curveType: 'trig', points: [],
      id: `trig_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
    };
  }

  // Uniform samples
  for (let x = xMin; x <= xMax; x += step) {
    xValues.add(x);
  }

  // Dense samples near each asymptote
  const nearDist = 0.5;
  const nearStep = 0.005;
  for (const a of asymptoteXValues) {
    for (let x = a - nearDist; x <= a + nearDist; x += nearStep) {
      if (x >= xMin && x <= xMax) {
        xValues.add(x);
      }
    }
  }

  const sortedX = [...xValues].sort((a, b) => a - b);
  const points = [];
  const ASYMPTOTE_THRESHOLD = 0.01;

  for (const x of sortedX) {
    const nearAsymptote = asymptoteXValues.some(a => Math.abs(x - a) < ASYMPTOTE_THRESHOLD);
    if (nearAsymptote) {
      points.push({ x, y: NaN });
      continue;
    }

    const y = evaluateTrigAt(parsedTrig, x);
    if (!isNaN(y) && isFinite(y)) {
      points.push({ x, y });
    } else {
      points.push({ x, y: NaN });
    }
  }

  return {
    type: 'curve',
    curveType: 'trig',
    points,
    id: `trig_${Date.now()}_${Math.random().toString(36).slice(2, 11)}`,
  };
}

/**
 * Generate asymptote elements (dashed vertical lines with pi-based labels)
 * for rendering on CoordinateGrid alongside the curve.
 *
 * Only labels the asymptotes within one period (centered on the phase shift)
 * to avoid overlapping labels on small graphs. Other asymptotes are shown
 * as unlabeled dashed lines.
 */
export function trigAsymptoteElements(parsedTrig, gridBounds) {
  if (!parsedTrig) return [];
  const { xMin = -7, xMax = 7 } = gridBounds || {};
  const allAsymptotes = computeAsymptotes(parsedTrig, xMin, xMax);
  const onePeriodAsymptotes = computeAsymptotesInOnePeriod(parsedTrig);

  // Build a set of asymptote x-values that are within one period (these get labels)
  const labeledSet = new Set();
  for (const x of onePeriodAsymptotes) {
    // Find the closest match in allAsymptotes
    const match = allAsymptotes.find(a => Math.abs(a - x) < 0.001);
    if (match !== undefined) labeledSet.add(match);
  }

  return allAsymptotes.map((x, i) => ({
    type: 'asymptote',
    x,
    label: labeledSet.has(x) ? formatPiValue(x) : null,
    id: `trig_asymptote_${i}_${Math.random().toString(36).slice(2, 8)}`,
  }));
}

/**
 * Generate all elements (curve + asymptotes) for a trig function variation.
 * Returns an array of elements to pass to CoordinateGrid's elements prop.
 */
export function trigGraphElements(parsedTrig, gridBounds, numSamples = 500) {
  if (!parsedTrig) return [];
  const curve = trigToCurveElement(parsedTrig, gridBounds, numSamples);
  const asymptotes = trigAsymptoteElements(parsedTrig, gridBounds);
  return [curve, ...asymptotes];
}

// ============================================
// GRAPH VARIATION GENERATION
// ============================================

/**
 * Normalize step1Types from config.
 */
export function normalizeStep1Types(config) {
  if (config.step1Types && Array.isArray(config.step1Types) && config.step1Types.length > 0) {
    return config.step1Types;
  }
  return ['period'];
}

/**
 * Generate 4 graph variations based on step1 types.
 * The correct graph is always the first element before shuffling.
 *
 * @param {Object} parsedTrig - The original parsed trig function
 * @param {string[]} step1Types - Array of step1 type strings
 * @param {number} seed - Random seed for consistent shuffling
 * @returns {{ variations: Array, correctIndex: number }}
 */
export function generateTrigGraphVariations(parsedTrig, step1Types, seed) {
  if (!parsedTrig) return { variations: [], correctIndex: 0 };
  const types = Array.isArray(step1Types) ? step1Types : [step1Types || 'period'];

  let variations = [];
  const t = types[0]; // Primary type drives the variation strategy

  switch (t) {
    case 'period':
      variations = generatePeriodVariations(parsedTrig);
      break;
    case 'amplitude':
      variations = generateAmplitudeVariations(parsedTrig);
      break;
    case 'phaseShift':
      variations = generatePhaseShiftVariations(parsedTrig);
      break;
    case 'verticalShift':
      variations = generateVerticalShiftVariations(parsedTrig);
      break;
    case 'yIntercept':
      variations = generateYInterceptVariations(parsedTrig);
      break;
    case 'verticalAsymptotes':
      variations = generateVerticalAsymptoteVariations(parsedTrig);
      break;
    default:
      variations = generatePeriodVariations(parsedTrig);
  }

  const { shuffled, originalIndex } = seededShuffle(variations, seed);

  return {
    variations: shuffled.map((v, i) => ({
      ...v,
      label: ['A', 'B', 'C', 'D'][i],
      isCorrect: i === originalIndex,
    })),
    correctIndex: originalIndex,
  };
}

/**
 * Build a new parsedTrig object with modified parameters.
 */
function buildTrig(original, overrides) {
  const merged = { ...original, ...overrides };
  const isTanCot = merged.funcType === 'tan' || merged.funcType === 'cot';
  merged.amplitude = Math.abs(merged.A);
  merged.period = isTanCot ? Math.PI / Math.abs(merged.B) : (2 * Math.PI) / Math.abs(merged.B);
  merged.phaseShift = -merged.C / merged.B;
  merged.verticalShift = merged.D;

  const yInt = merged.A * TRIG_FUNC_MAP[merged.funcType](merged.C) + merged.D;
  merged.yIntercept = isFinite(yInt) ? yInt : undefined;

  return merged;
}

/**
 * Period variations: all variations share the same period (B unchanged).
 * 1. Original
 * 2. Negate A (flip over midline)
 * 3. Phase shift by half-period
 * 4. Negate A + phase shift by half-period
 */
function generatePeriodVariations(parsedTrig) {
  const { A, B, C, D, funcType } = parsedTrig;
  const isTanCot = funcType === 'tan' || funcType === 'cot';
  const halfPeriodShift = isTanCot ? Math.PI / 2 : Math.PI; // in terms of Bx+C phase

  return [
    { trig: parsedTrig, variationType: 'original' },
    { trig: buildTrig(parsedTrig, { A: -A }), variationType: 'flipped' },
    { trig: buildTrig(parsedTrig, { C: C + halfPeriodShift }), variationType: 'shifted' },
    { trig: buildTrig(parsedTrig, { A: -A, C: C + halfPeriodShift }), variationType: 'flipped_shifted' },
  ];
}

/**
 * Amplitude variations: all share same |A| (sin/cos only).
 * 1. Original
 * 2. Negate A (reflection)
 * 3. Double period (B/2)
 * 4. sin↔cos swap
 */
function generateAmplitudeVariations(parsedTrig) {
  const { A, B, C, funcType } = parsedTrig;
  const swappedFunc = funcType === 'sin' ? 'cos' : 'sin';

  return [
    { trig: parsedTrig, variationType: 'original' },
    { trig: buildTrig(parsedTrig, { A: -A }), variationType: 'flipped' },
    { trig: buildTrig(parsedTrig, { B: B / 2 }), variationType: 'stretched' },
    { trig: buildTrig(parsedTrig, { funcType: swappedFunc }), variationType: 'swapped_func' },
  ];
}

/**
 * Phase shift variations: all share same phase shift (-C/B).
 * 1. Original
 * 2. Negate A
 * 3. Double period (B/2, C/2 to preserve phase shift -C/B)
 * 4. Negate A + double period
 */
function generatePhaseShiftVariations(parsedTrig) {
  const { A, B, C } = parsedTrig;

  return [
    { trig: parsedTrig, variationType: 'original' },
    { trig: buildTrig(parsedTrig, { A: -A }), variationType: 'flipped' },
    { trig: buildTrig(parsedTrig, { B: B / 2, C: C / 2 }), variationType: 'stretched' },
    { trig: buildTrig(parsedTrig, { A: -A, B: B / 2, C: C / 2 }), variationType: 'flipped_stretched' },
  ];
}

/**
 * Vertical shift variations: all share same D.
 * 1. Original
 * 2. Negate A
 * 3. Double period (B/2)
 * 4. Phase shift by quarter-period
 */
function generateVerticalShiftVariations(parsedTrig) {
  const { A, B, C, funcType } = parsedTrig;
  const isTanCot = funcType === 'tan' || funcType === 'cot';
  const quarterPeriodShift = isTanCot ? Math.PI / 4 : Math.PI / 2;

  return [
    { trig: parsedTrig, variationType: 'original' },
    { trig: buildTrig(parsedTrig, { A: -A }), variationType: 'flipped' },
    { trig: buildTrig(parsedTrig, { B: B / 2 }), variationType: 'stretched' },
    { trig: buildTrig(parsedTrig, { C: C + quarterPeriodShift }), variationType: 'shifted' },
  ];
}

/**
 * Y-intercept variations: all share same f(0) (sin/cos only).
 * 1. Original
 * 2. Negate A, adjust C to preserve f(0)
 * 3. Double period (B/2), adjust C to preserve f(0)
 * 4. sin↔cos swap, adjust C to preserve f(0)
 */
function generateYInterceptVariations(parsedTrig) {
  const { A, B, C, D, funcType } = parsedTrig;
  const f0 = A * TRIG_FUNC_MAP[funcType](C) + D;

  // For variation 2: -A * func(C2) + D = f0 → func(C2) = -func(C)
  // sin(π + C) = -sin(C) and cos(π + C) = -cos(C), so C2 = π + C works for both
  const negAC = Math.PI + C;
  // Verify: -A * func(negAC) + D should equal f0
  const v2Trig = buildTrig(parsedTrig, { A: -A, C: negAC });

  // For variation 3: A * func(C3) + D = f0 with B' = B/2 → need func(C3) = func(C)
  // Simplest: C3 = C (same f(0) since B doesn't affect f(0))
  const v3Trig = buildTrig(parsedTrig, { B: B / 2 });

  // For variation 4: swap sin↔cos
  const swappedFunc = funcType === 'sin' ? 'cos' : 'sin';
  // A * swappedFunc(C4) + D = f0 → swappedFunc(C4) = func(C)
  // For sin→cos: cos(C4) = sin(C) → C4 = π/2 - C
  // For cos→sin: sin(C4) = cos(C) → C4 = π/2 - C
  const swapC = Math.PI / 2 - C;
  const v4Trig = buildTrig(parsedTrig, { funcType: swappedFunc, C: swapC });

  return [
    { trig: parsedTrig, variationType: 'original' },
    { trig: v2Trig, variationType: 'flipped' },
    { trig: v3Trig, variationType: 'stretched' },
    { trig: v4Trig, variationType: 'swapped_func' },
  ];
}

/**
 * Vertical asymptote variations: asymptote positions preserved (B and C unchanged).
 * For tan/cot/csc/sec only.
 * 1. Original
 * 2. Negate A
 * 3. Shift D up
 * 4. Negate A + shift D
 */
function generateVerticalAsymptoteVariations(parsedTrig) {
  const { A, D } = parsedTrig;
  const dShift = Math.abs(A) > 1 ? 2 : 1;

  return [
    { trig: parsedTrig, variationType: 'original' },
    { trig: buildTrig(parsedTrig, { A: -A }), variationType: 'flipped' },
    { trig: buildTrig(parsedTrig, { D: D + dShift }), variationType: 'shifted_up' },
    { trig: buildTrig(parsedTrig, { A: -A, D: D + dShift }), variationType: 'flipped_shifted_up' },
  ];
}

// ============================================
// GRID AUTO-SCALING
// ============================================

export function suggestXMin(parsedTrig) {
  const val = -1.5 * parsedTrig.period + parsedTrig.phaseShift;
  return Math.max(Math.floor(val) - 1, -15);
}

export function suggestXMax(parsedTrig) {
  const val = 1.5 * parsedTrig.period + parsedTrig.phaseShift;
  return Math.min(Math.ceil(val) + 1, 15);
}

export function suggestYMin(parsedTrig) {
  const { funcType, A, D } = parsedTrig;
  if (funcType === 'sin' || funcType === 'cos') {
    return Math.floor(D - Math.abs(A)) - 2;
  }
  return Math.floor(D) - 6;
}

export function suggestYMax(parsedTrig) {
  const { funcType, A, D } = parsedTrig;
  if (funcType === 'sin' || funcType === 'cos') {
    return Math.ceil(D + Math.abs(A)) + 2;
  }
  return Math.ceil(D) + 6;
}

// ============================================
// SEEDED RANDOM UTILITIES
// ============================================

function seededShuffle(array, seed) {
  const rng = createSeededRandom(seed);
  const shuffled = [...array];
  let originalIndex = 0;

  for (let i = shuffled.length - 1; i > 0; i--) {
    const j = Math.floor(rng() * (i + 1));
    [shuffled[i], shuffled[j]] = [shuffled[j], shuffled[i]];

    if (originalIndex === i) originalIndex = j;
    else if (originalIndex === j) originalIndex = i;
  }

  return { shuffled, originalIndex };
}

function createSeededRandom(seed) {
  let state = seed;
  return () => {
    state = (state * 1103515245 + 12345) & 0x7fffffff;
    return state / 0x7fffffff;
  };
}

// ============================================
// LATEX CONVERSION
// ============================================

/**
 * Convert trig equation string to LaTeX.
 * Handles: pi → \pi, sin → \sin, fractions, etc.
 */
export function trigEquationToLatex(equation) {
  if (!equation) return '';

  // If the input already contains LaTeX commands, pass it through unchanged.
  if (/\\(frac|tan|sin|cos|cot|csc|sec|pi|left|right|cdot|times)\b/.test(equation)) {
    return equation.trim();
  }

  let latex = equation.trim();

  // Replace function names with LaTeX commands (must be done before other replacements)
  // Use word boundaries to avoid partial matches
  latex = latex.replace(/\bcsc\b/g, '\\csc');
  latex = latex.replace(/\bsec\b/g, '\\sec');
  latex = latex.replace(/\bcot\b/g, '\\cot');
  latex = latex.replace(/\bsin\b/g, '\\sin');
  latex = latex.replace(/\bcos\b/g, '\\cos');
  latex = latex.replace(/\btan\b/g, '\\tan');

  // Replace pi/π with \pi{}. The trailing {} prevents adjacent letters
  // (e.g. "πx" or "pix") from fusing into the \pi command, which would
  // make KaTeX render it as an unknown "\pix" token.
  latex = latex.replace(/π/g, '\\pi{}');
  latex = latex.replace(/\bpi\b/g, '\\pi{}');

  // Handle fraction coefficients like 1/2 before trig functions
  // Match patterns like "1/2\sin" or "-3/4\cos"
  latex = latex.replace(/-(\d+)\/(\d+)/g, '-\\frac{$1}{$2}');
  latex = latex.replace(/(?<![\d])(\d+)\/(\d+)/g, '\\frac{$1}{$2}');

  return latex;
}

/**
 * Format a numeric value for display, showing pi-based fractions when appropriate.
 */
export function formatPiValue(value) {
  if (value === undefined || value === null || isNaN(value)) return '—';
  if (value === 0) return '0';

  // Check if the value is a simple multiple of pi
  const piRatio = value / Math.PI;

  // Common pi fractions to check
  const piDenominators = [1, 2, 3, 4, 6];
  for (const d of piDenominators) {
    const n = Math.round(piRatio * d);
    if (Math.abs(piRatio - n / d) < 0.0001) {
      if (d === 1) {
        if (n === 1) return 'π';
        if (n === -1) return '-π';
        return `${n}π`;
      }
      if (n === 1) return `π/${d}`;
      if (n === -1) return `-π/${d}`;
      return `${n}π/${d}`;
    }
  }

  // Not a clean pi fraction — return decimal
  return Number.isInteger(value) ? value.toString() : value.toFixed(4);
}
